import { useEffect, useState, useMemo } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from "recharts";
import { parseISO, getHours, format, getMinutes, parse } from "date-fns";
import { DATE_STRING } from "../../community-hats-js-library/utils/dateFunctions";

export default function ProgressiveLineChart({
    data,
    width = 1000,
    height = 300,
    currentIndex,
    xValue,
    yValue,
    strokeColor
}) {
    const [actualData, setActualData] = useState(() =>
        data.map((d) => ({ ...d, [yValue]: null }))
    );

    // compute fixed min/max for y-axis
    const { minY, maxY } = useMemo(() => {
        const values = data.map((d) => d[yValue]).filter((v) => v != null);
        return {
            minY: Math.floor(Math.min(...values)),
            maxY: Math.ceil(Math.max(...values))
        };
    }, [data, yValue]);

    useEffect(() => {
        setActualData(
            data.map((d, i) => ({
                ...d,
                [yValue]: i > currentIndex ? null : d[yValue]
            }))
        );
    }, [currentIndex, data, yValue]);

    // build explicit ticks: midnight, 6:00, 18:00
    const ticks = useMemo(() => {
        return data
            .filter((d) => {
                const h = getHours(parse(d[xValue], DATE_STRING, new Date()));
                const m = getMinutes(parse(d[xValue], DATE_STRING, new Date()));

                return h + m === 0 || h + m === 12;
            })
            .map((d) => d[xValue]); // keep format consistent with data
    }, [data, xValue]);


    // custom tick rendering
    const CustomTick = ({ x, y, payload }) => {

        const date = parse(payload.value, DATE_STRING, new Date());
        const h = getHours(date)


        if (h === 0) {
            return (
                <text x={x} y={y + 15} textAnchor="middle" fill="#666">
                    {format(date, "MMM d")}
                </text>
            );
        }

        if (h === 12) {
            return (
                <text x={x} y={y + 10} textAnchor="middle" fill="#666" fontSize={16}>
                    {format(date, "H:mm")}
                </text>
            );
        }

        return null;
    };


    return (
        <div style={{ width, height }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}
                    margin={{ top: 20, right: 2, bottom: 2, left: 2 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={xValue} ticks={ticks} tick={<CustomTick />} interval={0} />
                    <YAxis domain={[minY, maxY]} ticks={[84, 96, maxY]} interval={0} />
                    <Tooltip
                        labelFormatter={(label) =>
                            format(parseISO(label), "MMM d, HH:mm")
                        }
                    />
                    <Line
                        type="monotone"
                        dataKey={yValue}
                        stroke={strokeColor}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                        activeDot={false}
                        data={actualData}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
